import * as Yup from "yup"

/**
 * Base address fields validation
 * https://docs.commercetools.com/api/types#baseaddress
 *
 * @returns {Yup}
 */
export function addressValidation() {
  return Yup.object({
    country: Yup.string().min(2).max(2).required(),
  })
}

function getByteLength(value) {
  return new TextEncoder().encode(value).length
}

function maxBytes(maxByteLength, message) {
  return Yup.string().test("max-bytes", message, (value) => !value || getByteLength(value) <= maxByteLength)
}

/**
 * Validates the address fields for a shipping address.
 *
 * @module addressValidation
 * @returns {Yup} - The Yup validation schema for the address fields.
 */
export const getShippingAddressSchema = ({
  ERROR_MSG_REQUIRED = "This field is required",
  CHARACTERS_MAX_LENGTH = "The maximum length is %s characters",
  ERROR_MSG_ONLY_DIGITS = "Only digits are allowed",
} = {}) => {
  return Yup.object().shape({
    firstName: maxBytes(30, CHARACTERS_MAX_LENGTH.replace("%s", "30")),
    lastName: Yup.string()
      .required(ERROR_MSG_REQUIRED)
      .concat(maxBytes(30, CHARACTERS_MAX_LENGTH.replace("%s", "30"))),
    streetName: Yup.string()
      .required(ERROR_MSG_REQUIRED)
      .concat(maxBytes(30, CHARACTERS_MAX_LENGTH.replace("%s", "30"))),
    postalCode: Yup.string()
      .required(ERROR_MSG_REQUIRED)
      .matches(/^\d+$/, ERROR_MSG_ONLY_DIGITS)
      .concat(maxBytes(5, CHARACTERS_MAX_LENGTH.replace("%s", "5"))),
    city: Yup.string()
      .required(ERROR_MSG_REQUIRED)
      .concat(maxBytes(30, CHARACTERS_MAX_LENGTH.replace("%s", "30"))),
    additionalAddressInfo: maxBytes(30, CHARACTERS_MAX_LENGTH.replace("%s", "30")),
    additionalStreetInfo: maxBytes(50, CHARACTERS_MAX_LENGTH.replace("%s", "50")),
  })
}

/**
 * Validates the address fields for a contact address.
 *
 * @module addressValidation
 * @returns {Yup} - The Yup validation schema for the address fields.
 */
export const getContactAddressSchema = ({
  ERROR_MSG_REQUIRED = "This field is required",
  ERROR_MSG_EMAIL_INVALID = "Invalid email address",
  CHARACTERS_MAX_LENGTH = "The maximum length is %s characters",
} = {}) => {
  return Yup.object({
    salutation: Yup.string().min(1, ERROR_MSG_REQUIRED).required(ERROR_MSG_REQUIRED),
    title: maxBytes(100, CHARACTERS_MAX_LENGTH.replace("%s", "100")),
    firstName: Yup.string()
      .required(ERROR_MSG_REQUIRED)
      .concat(maxBytes(100, CHARACTERS_MAX_LENGTH.replace("%s", "100"))),
    lastName: Yup.string()
      .required(ERROR_MSG_REQUIRED)
      .concat(maxBytes(100, CHARACTERS_MAX_LENGTH.replace("%s", "100"))),
    phone: maxBytes(20, CHARACTERS_MAX_LENGTH.replace("%s", "20")),
    mobile: maxBytes(20, CHARACTERS_MAX_LENGTH.replace("%s", "20")),
    fax: maxBytes(20, CHARACTERS_MAX_LENGTH.replace("%s", "20")),
    email: maxBytes(80, CHARACTERS_MAX_LENGTH.replace("%s", "80")).email(ERROR_MSG_EMAIL_INVALID),
  })
}
